/**
 * Search form validation, run before a request is sent.
 *
 * Why this exists, beyond tidiness. Several criteria are `int` in PDP: street
 * number, unit number and Property Key. The API drops a value it cannot coerce
 * rather than failing the request, so a GNAF PID typed into Property Key does
 * not narrow the search, it **widens** it, and the widened search then scans the
 * flood table until the query timeout fires. QA saw that as "the search took too
 * long" with no hint that the input was the cause.
 *
 * So these are not cosmetic warnings. Blocking an invalid value here is what
 * stops a typo becoming a one minute wait and an unexplained timeout.
 *
 * The legacy K2 form does the same thing with a modal alert reading "Please
 * ensure that the search criteria contains valid values". The wording below is
 * per field on purpose: the legacy modal never says which field is wrong.
 */

import type { SearchCriteria } from '../api/types'

/** Field keys that can carry a validation message. */
export type ValidationField =
  | 'postcode'
  | 'suburb'
  | 'streetName'
  | 'streetNumber'
  | 'unitNumber'
  | 'propertyKey'
  | 'strataPlanNumber'
  /** Not a field: the search as a whole is too broad to run. */
  | 'form'

export type ValidationErrors = Partial<Record<ValidationField, string>>

const DIGITS_ONLY = /^\d+$/

/**
 * Maximum lengths, matching `PerilSearchQuery` in server/app/models.py, which in
 * turn matches the procedures' declared parameter sizes.
 *
 * Checked here as well as there so an over-long value is a message against the
 * field rather than a 422 the SPA can only report generically. QA pasted a
 * hundred-character string into Strata Plan Number and got a timeout message,
 * which named the wrong problem entirely.
 */
export const MAX_LENGTHS = {
  propertyKey: 20,
  strataPlanNumber: 50,
  suburb: 100,
  streetName: 100,
  streetNumber: 20,
  unitNumber: 20,
} as const
/** Letters, spaces, apostrophes and hyphens. Covers O'CONNOR and BONDI-JUNCTION. */
const PLACE_NAME = /^[A-Za-z\s'\-.]+$/

/**
 * Australian postcodes are exactly four digits. PDP stores them as text, and
 * the procedure matches on that text, so `2OOO` with letter O simply matches
 * nothing and looks like missing data.
 */
function validatePostcode(value: string): string | undefined {
  if (!value) return undefined
  if (!DIGITS_ONLY.test(value)) return 'Postcode must be numbers only, for example 2216.'
  if (value.length !== 4) return 'Postcode must be four digits, for example 2216.'
  return undefined
}

/**
 * A whole-number criterion. PDP declares these as `int`, and the API drops
 * anything it cannot coerce, which widens the search instead of narrowing it.
 */
function validateWholeNumber(value: string, label: string): string | undefined {
  if (!value) return undefined
  if (!DIGITS_ONLY.test(value)) return `${label} must be a whole number.`
  return undefined
}

/**
 * A length message, or undefined. Checked before the shape, so a value that is
 * both too long and malformed reports the length: that is the one the
 * underwriter can see for themselves.
 */
function tooLong(value: string, max: number, label: string): string | undefined {
  if (value.length <= max) return undefined
  return `${label} cannot be longer than ${max} characters.`
}

function validatePlaceName(value: string, label: string): string | undefined {
  if (!value) return undefined
  if (DIGITS_ONLY.test(value)) {
    return `${label} does not look like a name. Did you mean to use another field?`
  }
  if (!PLACE_NAME.test(value)) {
    return `${label} must be letters only. Remove any numbers or symbols.`
  }
  return undefined
}

/**
 * Shortest Suburb or Street Name that counts as narrowing the search.
 *
 * PDP matches both with `like '%value%'`, so a single letter matches a large
 * share of every locality and street in Australia. QA typed one letter, pressed
 * Search, and watched it hang: the query runs for a minute before the timeout
 * fires, and the answer would have been useless anyway.
 *
 * Three rather than two, because two-letter suburbs and streets are vanishingly
 * rare and a two character substring is still most of the country.
 */
export const MIN_TEXT_CRITERION_LENGTH = 3

/**
 * True when the address criteria narrow the search enough to be worth running.
 *
 * State alone is not enough, and this is the reason: neither search procedure
 * bounds its match set, and the flood and bushfire tables hold 15.8 million
 * rows. A state-only search scans until the query timeout fires, roughly a
 * minute later, and returns nothing useful. QA reported exactly that.
 *
 * A Postcode is narrow on its own. Suburb and Street Name have to be long
 * enough to mean something, for the reason above.
 *
 * Note this is about whether *any* criterion narrows, not a rule per field. A
 * short Street Name alongside a real Suburb is fine and useful: it filters
 * further rather than widening.
 */
export function hasEnoughAddressCriteria(criteria: SearchCriteria): boolean {
  if (criteria.postcode.trim()) return true
  return [criteria.suburb, criteria.streetName].some(
    (value) => value.trim().length >= MIN_TEXT_CRITERION_LENGTH,
  )
}

/** True when something was typed, but nothing typed is long enough to narrow. */
function hasOnlyShortCriteria(criteria: SearchCriteria): boolean {
  const typed = [criteria.suburb, criteria.streetName].filter((value) => value.trim())
  return typed.length > 0 && !hasEnoughAddressCriteria(criteria)
}

export function validateCriteria(criteria: SearchCriteria): ValidationErrors {
  const errors: ValidationErrors = {}

  if (criteria.searchBy === 'property') {
    const propertyKeyValue = criteria.propertyKey.trim()
    const strataValue = criteria.strataPlanNumber.trim()

    // int in PDP, and compared against PDP.Property.PropertyKey. A GNAF PID
    // pasted here is the case QA hit: it cannot narrow, so the search widens.
    const propertyKey =
      tooLong(propertyKeyValue, MAX_LENGTHS.propertyKey, 'Property Key') ??
      validateWholeNumber(propertyKeyValue, 'Property Key')
    if (propertyKey) errors.propertyKey = propertyKey

    const strata =
      tooLong(strataValue, MAX_LENGTHS.strataPlanNumber, 'Strata Plan Number') ??
      (strataValue && !/^[A-Za-z0-9\s\-/]+$/.test(strataValue)
        ? 'Strata Plan Number must be letters and numbers only.'
        : undefined)
    if (strata) errors.strataPlanNumber = strata

    if (!criteria.propertyKey.trim() && !criteria.strataPlanNumber.trim()) {
      errors.form = 'Enter a Property Key or a Strata Plan Number to search.'
    }
    return errors
  }

  const postcode = validatePostcode(criteria.postcode.trim())
  if (postcode) errors.postcode = postcode

  const suburb =
    tooLong(criteria.suburb.trim(), MAX_LENGTHS.suburb, 'Suburb') ??
    validatePlaceName(criteria.suburb.trim(), 'Suburb')
  if (suburb) errors.suburb = suburb

  const streetName =
    tooLong(criteria.streetName.trim(), MAX_LENGTHS.streetName, 'Street Name') ??
    validatePlaceName(criteria.streetName.trim(), 'Street Name')
  if (streetName) errors.streetName = streetName

  const streetNumber =
    tooLong(criteria.streetNumber.trim(), MAX_LENGTHS.streetNumber, 'Street Number') ??
    validateWholeNumber(criteria.streetNumber.trim(), 'Street Number')
  if (streetNumber) errors.streetNumber = streetNumber

  const unitNumber =
    tooLong(criteria.unitNumber.trim(), MAX_LENGTHS.unitNumber, 'Unit No') ??
    validateWholeNumber(criteria.unitNumber.trim(), 'Unit No')
  if (unitNumber) errors.unitNumber = unitNumber

  if (!hasEnoughAddressCriteria(criteria)) {
    if (hasOnlyShortCriteria(criteria)) {
      // Distinct from the empty case: they did type something, so telling them
      // to enter a Suburb reads as though the application ignored them.
      errors.form =
        `Enter at least ${MIN_TEXT_CRITERION_LENGTH} characters of a Suburb or ` +
        'Street Name, or a Postcode. A shorter one matches too many records to be useful.'
    } else {
      errors.form =
        criteria.state === 'ALL'
          ? 'Enter a Postcode, Suburb or Street Name to search.'
          : 'A state on its own matches too many records. Add a Postcode, Suburb or Street Name.'
    }
  }

  return errors
}

export function hasErrors(errors: ValidationErrors): boolean {
  return Object.keys(errors).length > 0
}
